import type { AppMountContext } from "@tc-superpods/app-contract";
import {
  includeRegistryFallback,
  legacyTenantId,
  legacyTenantName,
  localRegistryUrls,
  normalizePathname,
  oidcAuthority,
  oidcClientId,
  publishedRegistryUrl,
  registryApiBaseUrl,
  useTenantRegistryApi
} from "./config.js";
import type {
  LoadedTenantRuntime,
  RegistryEntry,
  RequestedRoute,
  TenantBootstrapResult,
  TenantSummary,
  PreviewSession,
  Workspace
} from "./types";

type TimelineLogger = (step: string, details?: Record<string, unknown>) => void;

interface TenantTokenCacheEntry {
  token?: string;
  expiresAtMs?: number;
  refreshPromise?: Promise<string>;
}

const registryPromises = new Map<string, Promise<RegistryEntry[]>>();
const workspacesPromises = new Map<string, Promise<Workspace[]>>();
const tenantTokenCache = new Map<string, TenantTokenCacheEntry>();
const tenantTokenRefreshWindowMs = 2 * 60 * 1000;
// Legacy/no-tenant-API mode reads one `/registry.json` document for its app list,
// workspace list, and variables, so all runtime projections share one promise.
let runtimeRegistryDataPromise: Promise<RegistryDocument> | null = null;
// buildRegistry, buildTenantWorkspaces, and buildRuntimeRegistryData each independently need
// the local override file — memoizing the probe itself (not just each builder's own result)
// means a single bootstrap/refresh cycle only ever fetches it once, no matter how many of
// those builders run.
let localOverridePromise: Promise<LocalRegistryOverride | null> | null = null;

// The override file itself is workspace-wrapped (`Workspace[]`, each already
// carrying its expanded `apps`) — the same shape the real `/workspaces`
// endpoint returns — so a local override can shape both grouping and app
// entries at once.
interface LocalRegistryOverride {
  url: string;
  workspaces: Workspace[];
  variables: Record<string, string>;
}

interface RegistryDocument {
  workspaces: Workspace[];
  variables: Record<string, string>;
}

export async function fetchAccessibleTenants(
  identity: AppMountContext["identity"],
  log?: TimelineLogger
): Promise<TenantSummary[]> {
  log?.("tenant-list:request:start");
  const response = await fetch(`${registryApiBaseUrl}/me/tenants`, {
    cache: "no-store",
    headers: await createAuthorizedHeaders(identity)
  });
  log?.("tenant-list:request:complete", {
    status: response.status
  });

  if (!response.ok) {
    throw new Error(`Tenant list fetch failed with ${response.status}.`);
  }

  const tenants = await response.json();
  if (!isTenantSummaryList(tenants)) {
    throw new Error("Tenant list response did not match the expected shape.");
  }

  return tenants;
}

export async function buildTenantRegistry(
  tenantId: string,
  identity: AppMountContext["identity"],
  log?: TimelineLogger
): Promise<RegistryEntry[]> {
  let registryPromise = registryPromises.get(tenantId);

  if (!registryPromise) {
    registryPromise = buildRegistry(tenantId, identity, log);
    registryPromises.set(tenantId, registryPromise);
  }

  return registryPromise;
}

// Per-tenant workspace results are the source of truth for both workspace navigation
// and the flattened app registry. Keeping this cache shared avoids fetching the same
// workspace result twice during one tenant load.
export async function buildCachedTenantWorkspaces(
  tenantId: string,
  identity: AppMountContext["identity"],
  log?: TimelineLogger
): Promise<Workspace[]> {
  let workspacesPromise = workspacesPromises.get(tenantId);

  if (!workspacesPromise) {
    workspacesPromise = buildTenantWorkspaces(tenantId, identity, log);
    workspacesPromises.set(tenantId, workspacesPromise);
  }

  return workspacesPromise;
}

async function fetchRuntimeRegistryData(
  identity: AppMountContext["identity"],
  log?: TimelineLogger
): Promise<RegistryDocument> {
  if (!runtimeRegistryDataPromise) {
    runtimeRegistryDataPromise = buildRuntimeRegistryData(identity, log);
  }

  return runtimeRegistryDataPromise;
}

export function invalidateTenantRegistry(tenantId: string): void {
  registryPromises.delete(tenantId);
  workspacesPromises.delete(tenantId);
  runtimeRegistryDataPromise = null;
  localOverridePromise = null;
}

export async function refreshSelectedTenantRuntime(
  tenant: TenantSummary,
  requestedAppPath: string,
  identity: AppMountContext["identity"],
  log?: TimelineLogger
): Promise<LoadedTenantRuntime> {
  invalidateTenantRegistry(tenant.id);

  return loadSelectedTenantRuntime(tenant, requestedAppPath, identity, log);
}

export function isPreviewSession(): boolean {
  return readPreviewSession() !== undefined;
}

export async function loadTenantBootstrap(
  pathname: string,
  identity: AppMountContext["identity"],
  log?: TimelineLogger
): Promise<TenantBootstrapResult> {
  if (!useTenantRegistryApi) {
    const legacyTenant = createLegacyTenantSummary();
    const requestedRoute = parseRequestedRoute(pathname, [legacyTenant]);

    return {
      kind: "selected",
      tenants: [legacyTenant],
      tenant: legacyTenant,
      requestedAppPath: extractRequestedAppPath(requestedRoute)
    };
  }

  const preview = readPreviewSession();
  const tenants = await fetchAccessibleTenants(identity, log);

  if (tenants.length === 0) {
    return {
      kind: "access-denied",
      title: "Access denied",
      message:
        "No tenants were returned for your account. Contact the platform team if you believe this is incorrect."
    };
  }

  const requestedRoute = parseRequestedRoute(pathname, tenants);

  if (requestedRoute.kind === "invalid-tenant") {
    return {
      kind: "access-denied",
      title: "Access denied",
      message: `Tenant ${requestedRoute.tenantId} is not available for your account.`
    };
  }

  if (requestedRoute.kind === "tenant-qualified") {
    return {
      kind: "selected",
      tenants,
      tenant: requestedRoute.tenant,
      requestedAppPath: requestedRoute.appPath,
      preview
    };
  }

  if (tenants.length === 1) {
    return {
      kind: "selected",
      tenants,
      tenant: tenants[0],
      requestedAppPath: requestedRoute.appPath,
      preview
    };
  }

  return {
    kind: "selection-required",
    tenants,
    requestedAppPath: requestedRoute.appPath
  };
}

export async function loadSelectedTenantRuntime(
  tenant: TenantSummary,
  requestedAppPath: string,
  identity: AppMountContext["identity"],
  log?: TimelineLogger,
  preview?: PreviewSession
): Promise<LoadedTenantRuntime> {
  if (!useTenantRegistryApi) {
    const runtime = await fetchRuntimeRegistryData(identity, log);
    return {
      tenant: createLegacyTenantSummary(),
      registry: flattenWorkspaceApps(runtime.workspaces),
      workspaces: runtime.workspaces,
      variables: runtime.variables,
      tenantToken: () => getTenantToken(legacyTenantId, identity, log),
      requestedAppPath
    };
  }

  const setup = await fetchTenantSetup(tenant.id, identity, log);
  const local = await fetchLocalRegistryOverride();
  const workspaces = preview ? [] : mergeWorkspaces(
    setup.workspaces,
    local,
    (apps) => resolveLocalAppsForOrganizationTypes(apps, setup.organizationTypes)
  );
  const registry = preview
    ? [await fetchPreviewRegistryEntry(tenant.id, preview, identity, log)]
    : flattenWorkspaceApps(workspaces);
  return {
    tenant,
    registry,
    workspaces,
    variables: mergeRegistryVariables(setup.variables, local?.variables ?? {}),
    tenantToken: () => getTenantToken(tenant.id, identity, log),
    requestedAppPath: preview ? ensurePreviewAppPath(requestedAppPath, registry[0]) : requestedAppPath,
    preview
  };
}

export function getTenantToken(
  tenantId: string,
  identity: AppMountContext["identity"],
  log?: TimelineLogger
): Promise<string> {
  const now = Date.now();
  const cacheEntry = tenantTokenCache.get(tenantId) ?? {};
  tenantTokenCache.set(tenantId, cacheEntry);

  if (cacheEntry.token === undefined || cacheEntry.expiresAtMs === undefined || cacheEntry.expiresAtMs <= now) {
    return refreshTenantToken(tenantId, identity, log, cacheEntry);
  }

  if (cacheEntry.expiresAtMs - now < tenantTokenRefreshWindowMs && cacheEntry.refreshPromise === undefined) {
    // Return the still-valid token immediately while refreshing it for the next caller.
    void refreshTenantToken(tenantId, identity, log, cacheEntry).catch((error: unknown) => {
      log?.("tenant-token:background-refresh-failed", {
        message: error instanceof Error ? error.message : String(error),
        tenantId
      });
    });
  }

  return Promise.resolve(cacheEntry.token);
}

function refreshTenantToken(
  tenantId: string,
  identity: AppMountContext["identity"],
  log: TimelineLogger | undefined,
  cacheEntry: TenantTokenCacheEntry
): Promise<string> {
  if (cacheEntry.refreshPromise) {
    return cacheEntry.refreshPromise;
  }

  const refreshPromise = exchangeTenantToken(tenantId, identity, log)
    .then((token) => {
      cacheEntry.token = token;
      cacheEntry.expiresAtMs = extractTokenExpiryMs(token);
      return token;
    })
    .finally(() => {
      if (cacheEntry.refreshPromise === refreshPromise) {
        cacheEntry.refreshPromise = undefined;
      }
    });

  cacheEntry.refreshPromise = refreshPromise;
  return refreshPromise;
}

export async function fetchTenantWorkspaces(
  tenantId: string,
  identity: AppMountContext["identity"],
  log?: TimelineLogger
): Promise<Workspace[]> {
  log?.("tenant-workspaces:request:start", {
    tenantId
  });
  const response = await fetch(`${registryApiBaseUrl}/me/tenants/${encodeURIComponent(tenantId)}/workspaces`, {
    cache: "no-store",
    headers: await createAuthorizedHeaders(identity)
  });
  log?.("tenant-workspaces:request:complete", {
    status: response.status,
    tenantId
  });

  if (!response.ok) {
    throw new Error(`Tenant workspaces fetch failed with ${response.status}.`);
  }

  const workspaces = await response.json();
  if (!isWorkspaceList(workspaces)) {
    throw new Error("Tenant workspaces response did not match the expected shape.");
  }

  return workspaces;
}

export interface OrganizationType {
  type: string;
  org_id: string | null;
  short_name: string | null;
  is_current_user_active: boolean;
}

export interface TenantSetup {
  workspaces: Workspace[];
  variables: Record<string, string>;
  organizationTypes?: OrganizationType[];
}

export async function fetchTenantSetup(
  tenantId: string,
  identity: AppMountContext["identity"],
  log?: TimelineLogger
): Promise<TenantSetup> {
  log?.("tenant-setup:request:start", { tenantId });
  const response = await fetch(`${registryApiBaseUrl}/me/setup?tenantId=${encodeURIComponent(tenantId)}`, {
    cache: "no-store",
    headers: await createAuthorizedHeaders(identity),
    ...(useTenantRegistryApi ? {} : {
      credentials: "omit" as const,
      redirect: "error" as const,
      signal: AbortSignal.timeout(1_000)
    })
  });
  log?.("tenant-setup:request:complete", { status: response.status, tenantId });
  if (!response.ok) throw new Error(`Tenant setup fetch failed with ${response.status}.`);
  const setup = await response.json() as unknown;
  if (
    !isRecord(setup) ||
    !isWorkspaceList(setup.workspaces) ||
    (setup.variables !== undefined && !isStringMap(setup.variables)) ||
    (setup.organizationTypes !== undefined && !isOrganizationTypeList(setup.organizationTypes))
  ) throw new Error("Tenant setup response did not match the expected shape.");
  if (setup.organizationTypes) ensureUniqueOrganizationTypes(setup.organizationTypes);
  return {
    workspaces: setup.workspaces,
    variables: setup.variables ?? {},
    ...(setup.organizationTypes === undefined ? {} : { organizationTypes: setup.organizationTypes })
  };
}

function isWorkspaceList(value: unknown): value is Workspace[] {
  return Array.isArray(value) && value.every(isWorkspace);
}

function isWorkspace(value: unknown): value is Workspace {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.label === "string" &&
    Array.isArray(value.apps) &&
    value.apps.every(isRegistryEntry)
  );
}

function isStringMap(value: unknown): value is Record<string, string> {
  return isRecord(value) && Object.values(value).every((entry) => typeof entry === "string");
}

function isOrganizationTypeList(value: unknown): value is OrganizationType[] {
  return Array.isArray(value) && value.every((organizationType) => (
    isRecord(organizationType) &&
    typeof organizationType.type === "string" &&
    (typeof organizationType.org_id === "string" || organizationType.org_id === null) &&
    (typeof organizationType.short_name === "string" || organizationType.short_name === null) &&
    typeof organizationType.is_current_user_active === "boolean"
  ));
}

// Resolves which workspace an already tenant-relative appPath belongs to, and strips
// the workspace segment (if present) so the remainder can be matched against the
// registry exactly as before. An app can belong to more than one workspace, so the
// URL is the only unambiguous source of truth for which one is active; when the
// segment is missing (old bookmark, first-ever visit) fall back to the persisted
// preferred workspace if it's valid for the resolved app, else the first match.
export function resolveWorkspaceContext(
  appPath: string,
  workspaces: ReadonlyArray<Workspace>,
  registry: ReadonlyArray<RegistryEntry>,
  preferredWorkspaceId: string | null
): { workspaceId: string | null; appPath: string } {
  const normalized = normalizePathname(appPath);
  const segments = normalized === "/" ? [] : normalized.slice(1).split("/");
  const [firstSegment, ...rest] = segments;
  const matchedWorkspace = workspaces.find((workspace) => workspace.id === firstSegment);

  if (matchedWorkspace) {
    return {
      workspaceId: matchedWorkspace.id,
      appPath: rest.length === 0 ? "/" : `/${rest.join("/")}`
    };
  }

  const entry = resolveEntry([...registry], normalized);
  if (!entry) {
    return { workspaceId: preferredWorkspaceId, appPath: normalized };
  }

  const candidates = workspaces.filter((workspace) => workspace.apps.some((app) => app.id === entry.id));
  const preferred = candidates.find((workspace) => workspace.id === preferredWorkspaceId);

  return {
    workspaceId: preferred?.id ?? candidates[0]?.id ?? null,
    appPath: normalized
  };
}

export async function exchangeTenantToken(
  tenantId: string,
  identity: AppMountContext["identity"],
  log?: TimelineLogger
): Promise<string> {
  log?.("tenant-token:request:start", {
    tenantId
  });
  const form = new URLSearchParams({
    grant_type: "urn:ietf:params:oauth:grant-type:token-exchange",
    subject_token: await identity.token(),
    subject_token_type: "urn:ietf:params:oauth:token-type:access_token",
    client_id: oidcClientId,
    org_group_id: tenantId
  });
  const response = await fetch(`${oidcAuthority}/connect/token`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded"
    },
    body: form
  });
  log?.("tenant-token:request:complete", {
    status: response.status,
    tenantId
  });

  if (!response.ok) {
    throw new Error(`Tenant token exchange failed with ${response.status}.`);
  }

  const payload = await response.json();
  if (!isTokenExchangeResponse(payload)) {
    throw new Error("Tenant token exchange response did not match the expected shape.");
  }

  return payload.access_token;
}

async function buildRegistry(
  tenantId: string,
  identity: AppMountContext["identity"],
  log?: TimelineLogger
): Promise<RegistryEntry[]> {
  log?.("tenant-registry:start", {
    tenantId
  });
  const workspaces = await buildCachedTenantWorkspaces(tenantId, identity, log);
  const registry = flattenWorkspaceApps(workspaces);
  log?.("tenant-registry:published-loaded", {
    appIds: registry.map((entry) => entry.id),
    tenantId
  });

  return registry;
}

async function buildTenantWorkspaces(
  tenantId: string,
  identity: AppMountContext["identity"],
  log?: TimelineLogger,
  tolerateFailure = true
): Promise<Workspace[]> {
  log?.("tenant-workspaces:start", {
    tenantId
  });

  let setup: TenantSetup;
  try {
    setup = await fetchTenantSetup(tenantId, identity, log);
  } catch (error) {
    if (!tolerateFailure) {
      throw error;
    }

    // The workspace-accordion nav is a progressive enhancement over the
    // app-centric fallback (Sidebar renders a plain SideNav when there are no
    // workspaces) — a failing/unavailable setup endpoint shouldn't block
    // tenant selection or app mounting.
    log?.("tenant-workspaces:fetch-failed", {
      message: error instanceof Error ? error.message : String(error),
      tenantId
    });

    return [];
  }

  log?.("tenant-workspaces:published-loaded", {
    workspaceIds: setup.workspaces.map((workspace) => workspace.id),
    tenantId
  });
  const local = await fetchLocalRegistryOverride();
  log?.("tenant-workspaces:local-override-loaded", {
    workspaceIds: local?.workspaces.map((workspace) => workspace.id) ?? [],
    url: local?.url,
    tenantId
  });

  return mergeWorkspaces(
    setup.workspaces,
    local,
    (apps) => resolveLocalAppsForOrganizationTypes(apps, setup.organizationTypes)
  );
}

async function buildRuntimeRegistryData(
  identity: AppMountContext["identity"],
  log?: TimelineLogger
): Promise<RegistryDocument> {
  log?.("runtime-registry:start");
  const setupPromise = fetchTenantSetup(legacyTenantId, identity, log).catch((error: unknown) => {
    log?.("runtime-registry:setup-failed", {
      message: error instanceof Error ? error.message : String(error)
    });
    return { workspaces: [], variables: {} };
  });
  const published = await fetchPublishedWorkspaces(log);
  log?.("runtime-registry:published-loaded", {
    workspaceIds: published.workspaces.map((workspace) => workspace.id)
  });
  const local = await fetchLocalRegistryOverride();
  log?.("runtime-registry:local-override-loaded", {
    workspaceIds: local?.workspaces.map((workspace) => workspace.id) ?? [],
    url: local?.url
  });
  const setup = await setupPromise;
  const setupOverride: LocalRegistryOverride = {
    url: `${registryApiBaseUrl}/me/setup`,
    workspaces: setup.workspaces,
    variables: setup.variables
  };

  return {
    workspaces: mergeWorkspaces(mergeWorkspaces(published.workspaces, setupOverride), local),
    variables: mergeRegistryVariables(
      mergeRegistryVariables(published.variables, local?.variables ?? {}),
      setup.variables
    )
  };
}

async function fetchPreviewRegistryEntry(
  tenantId: string,
  preview: PreviewSession,
  identity: AppMountContext["identity"],
  log?: TimelineLogger
): Promise<RegistryEntry> {
  log?.("tenant-preview-registry:request:start", { appId: preview.appId, tenantId, version: preview.version });
  const response = await fetch(`${registryApiBaseUrl}/admin/tenants/${encodeURIComponent(tenantId)}/applications/${encodeURIComponent(preview.appId)}/versions/${encodeURIComponent(preview.version)}/preview`, {
    cache: "no-store",
    headers: await createAuthorizedHeaders(identity)
  });
  log?.("tenant-preview-registry:request:complete", { status: response.status, appId: preview.appId, tenantId, version: preview.version });
  if (!response.ok) throw new Error(`Preview registry entry fetch failed with ${response.status}.`);
  const payload = await response.json();
  if (!isRecord(payload) || !isRegistryEntry(payload.entry)) throw new Error("Preview registry response did not match the expected shape.");
  return payload.entry;
}

async function fetchPublishedWorkspaces(log?: TimelineLogger): Promise<RegistryDocument> {
  log?.("runtime-registry:request:start", {
    url: publishedRegistryUrl
  });
  const response = await fetch(publishedRegistryUrl, {
    cache: "no-store"
  });
  log?.("runtime-registry:request:complete", {
    status: response.status,
    url: publishedRegistryUrl
  });

  if (!response.ok) {
    throw new Error(`Runtime registry fetch failed with ${response.status}.`);
  }

  const payload = await response.json();
  const document = parseRegistryDocument(payload);
  if (!document) {
    throw new Error("Runtime registry response did not match the expected shape.");
  }

  return document;
}

async function fetchLocalRegistryOverride(): Promise<LocalRegistryOverride | null> {
  if (isPreviewSession()) {
    return null;
  }

  localOverridePromise ??= probeLocalRegistryOverride();

  return localOverridePromise;
}

async function probeLocalRegistryOverride(): Promise<LocalRegistryOverride | null> {
  for (const url of getLocalRegistryCandidateUrls()) {
    try {
      const response = await fetch(url, {
        cache: "no-store",
        signal: AbortSignal.timeout(1000)
      });

      if (!response.ok) {
        continue;
      }

      const payload = await response.json();
      const document = parseRegistryDocument(payload);
      if (document) {
        return { url, ...document };
      }
    } catch {
      // No local override is a normal case during everyday development.
    }
  }

  return null;
}

// In tenant API mode the fixed loopback candidates may not include the shell's own
// origin (localhost and 127.0.0.1 are distinct), so a loopback-hosted shell also
// tries its own /registry.json — last, because the shell's own copy is the fallback
// and app-owned fragments must win. Deployed shells never treat their own shipped
// registry.json as a local override, even when registry fallback stays enabled
// for developer loopback probing (internal.korenext).
export function getLocalRegistryCandidateUrls(): ReadonlyArray<string> {
  if (!useTenantRegistryApi || !includeRegistryFallback) return localRegistryUrls;

  try {
    const shellUrl = new URL(window.location.href);
    if (normalizeLoopbackHostname(shellUrl.hostname) !== "loopback") return localRegistryUrls;

    const sameOriginRegistryUrl = new URL(publishedRegistryUrl, shellUrl).href;
    return [
      ...localRegistryUrls.filter((url) => url !== sameOriginRegistryUrl),
      sameOriginRegistryUrl
    ];
  } catch {
    return localRegistryUrls;
  }
}

const legacyRegistryWorkspaceId = "local";
const legacyRegistryWorkspaceLabel = "Local";

// Accepts either format a registry.json (published or local-override) can be
// in: the current workspace-wrapped `Workspace[]`, or the pre-workspace flat
// `AvailableApp[]`. The old format is wrapped in a single synthetic "Local"
// workspace so both shapes are consumed identically from here on. Returns
// null when the payload matches neither shape.
export function parseRegistryDocument(payload: unknown): RegistryDocument | null {
  if (isWorkspaceOverrideList(payload)) {
    return { workspaces: payload, variables: {} };
  }

  if (isRegistry(payload)) {
    return {
      workspaces: [{ id: legacyRegistryWorkspaceId, label: legacyRegistryWorkspaceLabel, apps: payload }],
      variables: {}
    };
  }

  if (isRecord(payload) && isWorkspaceList(payload.workspaces) && (payload.variables === undefined || isStringMap(payload.variables))) {
    return { workspaces: payload.workspaces, variables: payload.variables ?? {} };
  }

  return null;
}

/** Merges defaults with overrides; values from the registry file take precedence. */
export function mergeRegistryVariables(
  defaults: Readonly<Record<string, string>>,
  overrides: Readonly<Record<string, string>>
): Record<string, string> {
  return { ...defaults, ...overrides };
}

function isWorkspaceOverrideList(value: unknown): value is Workspace[] {
  return Array.isArray(value) && value.every(isWorkspace);
}

function flattenWorkspaceApps(workspaces: ReadonlyArray<Workspace>): RegistryEntry[] {
  const byId = new Map<string, RegistryEntry>();

  for (const workspace of workspaces) {
    for (const app of workspace.apps) {
      byId.set(app.id, app);
    }
  }

  return [...byId.values()];
}

function mergeWorkspaces(
  published: ReadonlyArray<Workspace>,
  local: LocalRegistryOverride | null,
  resolveLocalApps?: (apps: ReadonlyArray<RegistryEntry>) => RegistryEntry[]
): Workspace[] {
  const merged = new Map<string, Workspace>();

  for (const workspace of published) {
    merged.set(workspace.id, workspace);
  }

  if (local) {
    for (const workspace of local.workspaces) {
      const publishedWorkspace = merged.get(workspace.id);
      const localAppIds = new Set(workspace.apps.map((app) => app.id));
      const preservedPublishedApps = publishedWorkspace?.apps.filter((app) => !localAppIds.has(app.id)) ?? [];
      const localApps = isRootRegistryUrl(local.url)
        ? workspace.apps
        : workspace.apps.map((app) => ({
          ...app,
            registrySource: "local-override" as const,
            registrySourceUrl: local.url
          }));
      const resolvedLocalApps = resolveLocalApps ? resolveLocalApps(localApps) : localApps;
      const apps = [...resolvedLocalApps, ...preservedPublishedApps];

      if (resolveLocalApps && apps.length === 0) {
        merged.delete(workspace.id);
      } else {
        merged.set(workspace.id, { ...workspace, apps });
      }
    }
  }

  return [...merged.values()];
}

export function resolveLocalAppsForOrganizationTypes(
  apps: ReadonlyArray<RegistryEntry>,
  organizationTypes: ReadonlyArray<OrganizationType> | undefined
): RegistryEntry[] {
  if (organizationTypes === undefined) {
    return [...apps];
  }

  const typesByName = ensureUniqueOrganizationTypes(organizationTypes);

  return apps.flatMap((app) => {
    const organizationTypeName = app.organization_type;
    if (!organizationTypeName) return [app];

    const unresolvedApp = withoutLocalOrganizationId(app);
    const organizationType = typesByName.get(organizationTypeName);

    if (!organizationType) return [unresolvedApp];
    const organizationId = organizationType.org_id;
    const organizationShortName = organizationType.short_name;
    if (!organizationType.is_current_user_active || !organizationId || !organizationShortName) return [];

    return [{
      ...unresolvedApp,
      entry: unresolvedApp.entry
        .split("{organizationId}").join(encodeURIComponent(organizationId))
        .split("{organizationShortName}").join(encodeURIComponent(organizationShortName)),
      organizationId
    }];
  });
}

function withoutLocalOrganizationId(app: RegistryEntry): RegistryEntry {
  const unresolvedApp = { ...app };
  delete unresolvedApp.organizationId;
  return unresolvedApp;
}

function ensureUniqueOrganizationTypes(
  organizationTypes: ReadonlyArray<OrganizationType>
): ReadonlyMap<string, OrganizationType> {
  const typesByName = new Map<string, OrganizationType>();

  for (const organizationType of organizationTypes) {
    if (typesByName.has(organizationType.type)) {
      throw new Error(`Tenant setup response contained duplicate organization type ${organizationType.type}.`);
    }
    typesByName.set(organizationType.type, organizationType);
  }

  return typesByName;
}

function isRootRegistryUrl(url: string): boolean {
  try {
    const registryUrl = new URL(url);
    const shellUrl = new URL(window.location.href);

    return (
      registryUrl.protocol === shellUrl.protocol &&
      registryUrl.port === shellUrl.port &&
      normalizeLoopbackHostname(registryUrl.hostname) === normalizeLoopbackHostname(shellUrl.hostname)
    );
  } catch {
    return false;
  }
}

function normalizeLoopbackHostname(hostname: string): string {
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1" || hostname === "[::1]"
    ? "loopback"
    : hostname;
}

export function resolveEntry(registry: RegistryEntry[], pathname: string): RegistryEntry | undefined {
  return registry.find((entry) => pathname === entry.route || pathname.startsWith(`${entry.route}/`));
}

export function resolveInitialEntry(workspaces: ReadonlyArray<Workspace>): RegistryEntry | undefined {
  return workspaces[0]?.apps[0];
}

export function parseRequestedRoute(
  pathname: string,
  tenants: ReadonlyArray<TenantSummary>
): RequestedRoute {
  const normalizedPath = normalizePathname(pathname);

  if (normalizedPath === "/") {
    return {
      kind: "root",
      appPath: "/"
    };
  }

  const pathSegments = normalizedPath.slice(1).split("/");
  const [firstSegment, ...remainingSegments] = pathSegments;
  const matchingTenant = tenants.find((tenant) => tenant.id === firstSegment);

  if (matchingTenant) {
    return {
      kind: "tenant-qualified",
      tenant: matchingTenant,
      appPath: remainingSegments.length === 0 ? "/" : `/${remainingSegments.join("/")}`
    };
  }

  if (pathSegments.length > 1) {
    return {
      kind: "invalid-tenant",
      tenantId: firstSegment
    };
  }

  return {
    kind: "unqualified",
    appPath: normalizedPath
  };
}

export function getCurrentAppPath(
  pathname: string,
  tenants: ReadonlyArray<TenantSummary>
): string {
  const requestedRoute = parseRequestedRoute(pathname, tenants);

  if (requestedRoute.kind === "tenant-qualified") {
    return normalizePathname(requestedRoute.appPath);
  }

  if (requestedRoute.kind === "root" || requestedRoute.kind === "unqualified") {
    return normalizePathname(requestedRoute.appPath);
  }

  return "/";
}

export interface ResolvedDestination {
  path: string | null;
  preserveLocationState: boolean;
}

export function resolveDestination(
  tenantId: string,
  requestedAppPath: string,
  registry: ReadonlyArray<RegistryEntry>,
  workspaces: ReadonlyArray<Workspace>
): ResolvedDestination {
  const normalizedPath = normalizePathname(requestedAppPath);
  const requestedWorkspaceId = normalizedPath === "/" ? null : normalizedPath.slice(1).split("/")[0];
  const requestedWorkspace = workspaces.find((workspace) => workspace.id === requestedWorkspaceId);
  const resolved = resolveWorkspaceContext(normalizedPath, workspaces, registry, null);
  const entry = resolveEntry([...registry], resolved.appPath);
  const isAvailable = entry !== undefined && (
    requestedWorkspace === undefined || requestedWorkspace.apps.some((app) => app.id === entry.id)
  );
  const destinationPath = normalizedPath === "/" || !isAvailable
    ? resolveInitialEntry(workspaces)?.route
    : normalizedPath;

  return {
    path: destinationPath ? toTenantPath(tenantId, destinationPath, []) : null,
    preserveLocationState: destinationPath !== undefined && normalizedPath !== "/" && isAvailable
  };
}

export function resolveDestinationPath(
  tenantId: string,
  requestedAppPath: string,
  registry: ReadonlyArray<RegistryEntry>,
  workspaces: ReadonlyArray<Workspace>
): string | null {
  return resolveDestination(tenantId, requestedAppPath, registry, workspaces).path;
}

export function appendLocationSuffix(pathname: string, search: string, hash: string): string {
  return `${pathname}${search}${hash}`;
}

export function toBrowserPath(
  route: string,
  tenantId: string | null,
  tenants: ReadonlyArray<TenantSummary>,
  workspaceId?: string | null
): string {
  if (!tenantId) {
    return normalizePathname(route);
  }

  const tenantPath = toTenantPath(tenantId, route, tenants);

  return workspaceId ? insertWorkspaceSegment(tenantPath, tenantId, workspaceId) : tenantPath;
}

// Splices the workspace id in as the path segment right after the tenant id, mirroring
// how the tenant id itself is already threaded through every generated href. Left
// untouched if the path isn't tenant-qualified after all, or already carries this
// workspace id in that position.
function insertWorkspaceSegment(tenantPath: string, tenantId: string, workspaceId: string): string {
  const segments = tenantPath === "/" ? [] : tenantPath.slice(1).split("/");
  const [firstSegment, secondSegment, ...rest] = segments;

  if (firstSegment !== tenantId || secondSegment === workspaceId) {
    return tenantPath;
  }

  const remainder = [secondSegment, ...rest].filter((segment): segment is string => segment !== undefined);

  return remainder.length > 0
    ? `/${tenantId}/${workspaceId}/${remainder.join("/")}`
    : `/${tenantId}/${workspaceId}`;
}

function toTenantPath(
  tenantId: string,
  route: string,
  tenants: ReadonlyArray<TenantSummary>
): string {
  const normalizedRoute = normalizePathname(route);
  const routeSegments = normalizedRoute === "/" ? [] : normalizedRoute.slice(1).split("/");
  const firstSegment = routeSegments[0];
  const explicitTenant = tenants.find((tenant) => tenant.id === firstSegment);

  if (explicitTenant) {
    return normalizedRoute;
  }

  const normalizedTenantBase = normalizePathname(`/${tenantId}`);

  if (normalizedRoute === normalizedTenantBase || normalizedRoute.startsWith(`${normalizedTenantBase}/`)) {
    return normalizedRoute;
  }

  return normalizedRoute === "/"
    ? normalizedTenantBase
    : `${normalizedTenantBase}${normalizedRoute}`;
}

export function isRegistry(value: unknown): value is RegistryEntry[] {
  return Array.isArray(value) && value.every(isRegistryEntry);
}

function isRegistryEntry(value: unknown): value is RegistryEntry {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.id === "string" &&
    typeof value.route === "string" &&
    typeof value.entry === "string" &&
    typeof value.version === "string" &&
    (value.type === undefined || value.type === null || value.type === "" || value.type === "app" || value.type === "legacy" || value.type === "static") &&
    (value.navLabel === undefined || typeof value.navLabel === "string") &&
    (value.organization_type === undefined || typeof value.organization_type === "string") &&
    (value.organizationId === undefined || typeof value.organizationId === "string") &&
    (value.category === undefined || typeof value.category === "string") &&
    (value.description === undefined || typeof value.description === "string") &&
    (value.icon === undefined || typeof value.icon === "string") &&
    (value.showTopbar === undefined || typeof value.showTopbar === "boolean") &&
    (value.entryType === undefined || value.entryType === "global" || value.entryType === "module") &&
    (value.registrySource === undefined || value.registrySource === "local-override") &&
    (value.registrySourceUrl === undefined || typeof value.registrySourceUrl === "string")
  );
}

function isTenantSummaryList(value: unknown): value is TenantSummary[] {
  return Array.isArray(value) && value.every(isTenantSummary);
}

function isTenantSummary(value: unknown): value is TenantSummary {
  return isRecord(value) && typeof value.id === "string" && typeof value.name === "string";
}

function isTokenExchangeResponse(value: unknown): value is { access_token: string } {
  return isRecord(value) && typeof value.access_token === "string";
}

function extractTokenExpiryMs(token: string): number {
  const payloadSegment = token.split(".")[1];
  if (!payloadSegment) {
    throw new Error("Tenant token exchange response contained a token without a JWT payload.");
  }

  try {
    const normalizedPayload = payloadSegment.replace(/-/gu, "+").replace(/_/gu, "/");
    const paddedPayload = normalizedPayload.padEnd(Math.ceil(normalizedPayload.length / 4) * 4, "=");
    const payload = JSON.parse(new TextDecoder().decode(Uint8Array.from(atob(paddedPayload), (character) => character.charCodeAt(0)))) as unknown;
    if (!isRecord(payload) || typeof payload.exp !== "number" || !Number.isFinite(payload.exp)) {
      throw new Error("Tenant token exchange response contained a token without a valid exp claim.");
    }

    return payload.exp * 1000;
  } catch (error) {
    if (error instanceof Error && error.message.includes("without a valid exp claim")) {
      throw error;
    }

    throw new Error("Tenant token exchange response contained an unreadable JWT payload.");
  }
}

function ensurePreviewAppPath(requestedAppPath: string, entry: RegistryEntry): string {
  const normalizedPath = normalizePathname(requestedAppPath);
  return normalizedPath === "/" ? entry.route : normalizedPath;
}

function readPreviewSession(): PreviewSession | undefined {
  const params = new URLSearchParams(window.location.search);
  const appId = params.get("knPreviewAppId");
  const version = params.get("knPreviewVersion");
  return appId && version ? { appId, version } : undefined;
}

function extractRequestedAppPath(requestedRoute: RequestedRoute): string {
  return requestedRoute.kind === "tenant-qualified" ||
    requestedRoute.kind === "root" ||
    requestedRoute.kind === "unqualified"
    ? requestedRoute.appPath
    : "/";
}

function createLegacyTenantSummary(): TenantSummary {
  return {
    id: legacyTenantId,
    name: legacyTenantName
  };
}

async function createAuthorizedHeaders(identity: AppMountContext["identity"]): Promise<HeadersInit> {
  return {
    Authorization: `Bearer ${await identity.token()}`
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
